import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import {
  CODE_TO_USAGE_TYPE,
  QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
} from "../../utils/questionnaireQuestionnaireConstants";
import { optionToSlugOrFallback } from "../../utils/questionnaireQuestionnaireTransformers";
import { parseQuestionOptions } from "src-next/shared/utils/questionnaireOptions";
import type { QuestionnaireQuestionPayload } from "../../types";
import type { QuestionnaireResponsePayload } from "../../questionnaire-respond/types";
import type {
  QuestionnaireResponseViewModel,
  QuestionnaireResponseViewRow,
} from "../types";

dayjs.locale("pt-br");

const USAGE_LABEL_PT: Record<
  "triage" | "preConsultation" | "postConsultation" | "attendance",
  string
> = {
  triage: "Triagem",
  preConsultation: "Pré-consulta",
  postConsultation: "Pós-consulta",
  attendance: "Acompanhamento",
};

const RESPONSE_STATUS_PT: Record<string, string> = {
  "in-progress": "Em andamento",
  completed: "Concluído",
  amended: "Alterado",
  "entered-in-error": "Registrado por engano",
  stopped: "Interrompido",
};

function resolveUsageLabelFromFhirExtension(
  extension: Array<{ url?: string; valueCode?: string }> | undefined,
): string {
  const usageExtension = extension?.find(
    (e) => e.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
  );
  const code = usageExtension?.valueCode;
  if (!code) return "Uso não informado";
  const usage = CODE_TO_USAGE_TYPE[code];
  if (usage && USAGE_LABEL_PT[usage]) return USAGE_LABEL_PT[usage];
  return "Uso não informado";
}

function formatRespondent(subject?: { reference?: string }): string {
  const ref = subject?.reference?.trim();
  if (!ref) return "Respondente não informado";
  if (ref.startsWith("Patient/")) return "Paciente";
  return "Respondente identificado";
}

function formatAuthored(iso: string | undefined): string {
  if (!iso?.trim()) return "Data não informada";
  const parsed = dayjs(iso);
  if (!parsed.isValid()) return "Data não informada";
  return `Respondido em ${parsed.format("DD/MM/YYYY [às] HH:mm")}`;
}

function formatResponseStatus(status: string | undefined): string {
  if (!status?.trim()) return "Status não informado";
  return RESPONSE_STATUS_PT[status] ?? status;
}

function formatAnswerForQuestion(
  question: QuestionnaireQuestionPayload,
  responseItem: QuestionnaireResponsePayload["item"][number] | undefined,
): { label: string; isEmpty: boolean } {
  if (!responseItem?.answer?.length) {
    return { label: "Sem resposta", isEmpty: true };
  }

  const options = parseQuestionOptions(question.optionsCsv);
  const optionByLowerDisplay = new Map(
    options.map((option) => [option.toLowerCase(), option] as const),
  );
  const optionByCode = new Map(
    options.map((option, index) => [
      optionToSlugOrFallback(option, index),
      option,
    ]),
  );

  const answerLabels = responseItem.answer
    .map((answer) => {
      if ("valueCoding" in answer && answer.valueCoding) {
        const display = answer.valueCoding.display?.trim();
        if (display) return display;

        const code = answer.valueCoding.code?.trim();
        if (!code) return "";

        return optionByCode.get(code) ?? code;
      }

      if (
        "valueBoolean" in answer &&
        typeof answer.valueBoolean === "boolean"
      ) {
        return answer.valueBoolean ? "Sim" : "Não";
      }

      if ("valueString" in answer && typeof answer.valueString === "string") {
        const stringValue = answer.valueString.trim();
        if (!stringValue) return "";
        const byDisplay = optionByLowerDisplay.get(stringValue.toLowerCase());
        return byDisplay ?? stringValue;
      }

      return "";
    })
    .filter((answerLabel) => answerLabel.length > 0);

  if (answerLabels.length === 0) {
    return { label: "Sem resposta", isEmpty: true };
  }

  if (question.type === "multipleChoice") {
    return { label: answerLabels.join(", "), isEmpty: false };
  }

  return { label: answerLabels[0], isEmpty: false };
}

export interface BuildQuestionnaireResponseViewModelInput {
  questionnaireTitle: string;
  questionnaireDescription: string;
  questionnaireUsageLabel: string;
  questions: QuestionnaireQuestionPayload[];
  response: QuestionnaireResponsePayload & { status?: string };
}

export function buildQuestionnaireResponseViewModel(
  input: BuildQuestionnaireResponseViewModelInput,
): QuestionnaireResponseViewModel {
  const byLinkId = new Map(
    input.response.item.map((it) => [it.linkId, it] as const),
  );

  const rows: QuestionnaireResponseViewRow[] = input.questions.map(
    (question, index) => {
      const responseItem = byLinkId.get(question.id);
      const { label, isEmpty } = formatAnswerForQuestion(
        question,
        responseItem,
      );

      return {
        order: index + 1,
        questionLabel: question.label.trim() || "Nova pergunta",
        answerLabel: label,
        isRequired: question.isRequired,
        isAnswerEmpty: isEmpty,
      };
    },
  );

  return {
    questionnaireTitle: input.questionnaireTitle.trim() || "Questionário",
    questionnaireUsageLabel: input.questionnaireUsageLabel,
    questionnaireDescription: input.questionnaireDescription.trim(),
    respondentSummary: formatRespondent(input.response.subject),
    respondedAtLabel: formatAuthored(input.response.authored),
    statusLabel: formatResponseStatus(input.response.status),
    rows,
  };
}

export { resolveUsageLabelFromFhirExtension };
